// @flow
import React, {useState, useEffect, useRef, useCallback} from 'react';
import {List} from 'immutable';
import PropTypes from 'prop-types';
import {Divider, Typography} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import CustomModal from '../../../../../elements/CustomModal/CustomModal';
import {colors} from '../../../../../assets/styles/colors';

import CustomDraggable from '../../../../../elements/CustomDraggable/CustomDraggable';
import PromotionRow from './PromotionRow';
import {useIsFeatureInTier} from '../../../../../utils/component';

import type {SinglePromotion} from '../../../../../entities';
type Props = {|
  isSaving?: boolean,
  open?: boolean,
  handleClose: () => void,
  promotionsArr?: SinglePromotion[],
  sortPromotion: ({sortPromotionRequest: {[key: string]: any}}) => void,
|};

const useStyles = makeStyles(theme => ({
  containerModal: {
    '& .MuiDialog-paper': {
      padding: `${theme.spacing(2, 4)}!important`,
    },
  },
  sortModal: {
    width: 600,
    maxWidth: '100%',
    [theme.breakpoints.down('xs')]: {
      maxWidth: '100%',
      overflowX: 'hidden',
    },
  },
  titleModal: {
    fontStyle: 'normal',
    fontWeight: 580,
    fontSize: '18px',
    lineHeight: '24px',
    color: '#313135',
    textAlign: 'left',
  },
  subtitleModal: {
    color: colors.grey700,
    fontSize: 14,
  },
  divider: {
    margin: theme.spacing(2, 0),
  },
  promotionsList: {
    overflowY: 'auto',
  },
}));

export type Movement = {
  init?: {pos: number, id: string},
  end?: {pos: number, id: string},
};

export default function SortPromotionModal(props: Props) {
  const {handleClose, open, promotionsArr, sortPromotion, isSaving} = props;
  const classes = useStyles();

  const hasStorePromotionFeature = useIsFeatureInTier('STORE_PROMOTION');

  const [promotions, setPromotions] = useState();
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [itemDragged, setItemDragged] = useState();
  const [movement, setMovement] = useState<Movement | null>(null);

  const itemDraggedRef = useRef();

  useEffect(() => {
    if (promotionsArr && promotionsArr.length) {
      const filtered = promotionsArr.filter(p => p.enabled);
      setPromotions(filtered);
    }
  }, [promotionsArr]);

  const handleDragStart = (e, item) => {
    const dragImg = document.createElement('img');
    dragImg.src =
      'data:image/gif;base64,R0lGODlhAQABAAAAACH5BAEKAAEALAAAAAABAAEAAAICTAEAOw==';
    e.dataTransfer.setDragImage(dragImg, 0, 0);
    setIsDragging(true);
    setItemDragged(item);
    setMovement({
      init: {
        pos: getIndexOfElement(promotions, item),
        id: item.id,
      },
      end: undefined,
    });
    itemDraggedRef.current = e.target;
  };

  const getIndexOfElement = (arr = [], item: {id: string}) =>
    arr.findIndex(el => el.id === item.id);

  const handleDragEnter = (e, item) => {
    if (itemDraggedRef.current !== e.target) {
      if (itemDragged) {
        const pos = getIndexOfElement(promotions, item);
        setMovement({
          ...movement,
          end: {
            pos: pos,
            id: item.id,
          },
        });
        setPromotions(prev => {
          const indexElementSort = prev
            ? prev.findIndex(el => el.id === itemDragged.id)
            : -1;
          const sList = List(prev)
            .delete(indexElementSort)
            .insert(pos, itemDragged);
          const s = sList.toArray();
          return s;
        });
      }
    }
  };

  const onDragEnd = useCallback(() => {
    if (itemDragged && itemDraggedRef.current && movement && promotions) {
      setIsDragging(false);
      setItemDragged(null);

      const {init, end} = movement;
      if (init && end && init.pos !== end.pos) {
        sortPromotion({
          sortPromotionRequest: movement,
        });
        setMovement(null);
      }
      //$FlowFixMe
      itemDraggedRef.current.removeEventListener('dragend', onDragEnd);
      itemDraggedRef.current = null;
    }
  }, [itemDragged, movement, promotions, sortPromotion]);

  useEffect(() => {
    if (isDragging && itemDraggedRef && itemDraggedRef.current) {
      itemDraggedRef.current.addEventListener('dragend', onDragEnd);
      return () => {
        itemDraggedRef &&
          itemDraggedRef.current &&
          itemDraggedRef.current.removeEventListener('dragend', onDragEnd);
      };
    }
  }, [isDragging, onDragEnd]);

  return (
    <CustomModal
      open={open}
      handleCloseModal={handleClose}
      className={classes.containerModal}
    >
      <div className={classes.sortModal}>
        <h1 className={classes.titleModal}>Ordenar Promociones</h1>
        <Typography className={classes.subtitleModal}>
          Mueve las promociones en el orden que desees y se vera reflejado en tu
          tienda.{' '}
        </Typography>
        <Divider className={classes.divider} />

        <div className={classes.promotionsList}>
          {promotions &&
            promotions.map(p => (
              <CustomDraggable
                disabled={!hasStorePromotionFeature}
                key={p.id}
                className={classes.rowPromotion}
                item={p}
                handleDragStart={handleDragStart}
                handleDragEnter={handleDragEnter}
                isSaving={isSaving}
                isDragging={isDragging}
                dragItemId={itemDragged ? itemDragged.id : '-1'}
              >
                <PromotionRow promotion={p} />
              </CustomDraggable>
            ))}
        </div>
      </div>
    </CustomModal>
  );
}

SortPromotionModal.propTypes = {
  isSaving: PropTypes.bool,
  open: PropTypes.bool,
  handleClose: PropTypes.func.isRequired,
  promotionsArr: PropTypes.array,
  sortPromotion: PropTypes.func.isRequired,
};
